import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Clock, CheckCircle2, BookOpen, Sparkles, Star, ArrowRight } from 'lucide-react';
import { FloatingDoodles, DoodleStrip } from '../components/Doodles';

const HERO_IMAGES = [
  {
    src: "https://static.prod-images.emergentagent.com/jobs/5f8564e9-242d-41fd-a450-5251f2a2d619/images/7fd110883864b6500cc0a5839a23a027d95ce84f3925fe2d14d5a07645760512.png",
    name: "Cappy the Capybara",
    subtitle: "Your calm reading buddy",
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/5f8564e9-242d-41fd-a450-5251f2a2d619/images/f818918fa151e86d7d44c85e085f6f22455234fa280a9ab13d0b9a465f011cd1.png",
    name: "Pepper the Red Panda",
    subtitle: "Your study companion",
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/5f8564e9-242d-41fd-a450-5251f2a2d619/images/bfbe3725430ecb83fba0726c1ef04db35ddecefe66a3953de0f95876a4b1c51d.png",
    name: "Fern the Frog",
    subtitle: "Your focus timer master",
  },
  {
    src: "https://static.prod-images.emergentagent.com/jobs/5f8564e9-242d-41fd-a450-5251f2a2d619/images/7577285de24c6174152b5c4466b075baa64a6e40180f63f418d69d3f8c9fc01b.png",
    name: "Biscuit the Corgi",
    subtitle: "Your break time friend",
  },
];

const FEATURES = [
  { icon: CheckCircle2, title: "Trafikljus-tasks", desc: "Sortera efter brådska: idag, denna vecka eller senare", color: "#B9FBC0" },
  { icon: Clock, title: "Focus Timer", desc: "Pomodoro & custom timers with pet encouragement", color: "#A2D2FF" },
  { icon: Heart, title: "Virtual Pet", desc: "Evolve your companion through 5 adorable stages", color: "#FFC8DD" },
  { icon: BookOpen, title: "Bullet Journal", desc: "Daily log, habit tracker, mood tracker & doodle pad", color: "#CDB4DB" },
  { icon: Star, title: "Points & Streaks", desc: "Earn points, keep streaks, buy accessories", color: "#FDF0D5" },
  { icon: Sparkles, title: "Schedule & Lock", desc: "Daily routines with app lock until tasks are done", color: "#B9FBC0" },
];

export default function LandingPage() {
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-transparent dark:bg-[#1a1a2e] relative overflow-hidden">
      <FloatingDoodles />

      {/* Hero Section */}
      <section className="relative z-10 max-w-6xl mx-auto px-4 pt-8 pb-16">
        {/* Nav */}
        <nav className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FFC8DD] border-3 border-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] flex items-center justify-center">
              <Heart className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <h1 className="text-2xl font-bold font-['Fredoka']">Focus Critter</h1>
          </div>
          <div className="flex gap-3">
            <Link to="/login" className="neo-btn px-5 py-2 bg-white" data-testid="landing-login-btn">
              Sign In
            </Link>
            <Link to="/register" className="neo-btn px-5 py-2 bg-[#B9FBC0]" data-testid="landing-register-btn">
              Get Started
            </Link>
          </div>
        </nav>

        {/* Hero Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          {/* Left — Text */}
          <div>
            <p className="text-sm font-bold uppercase tracking-widest text-[#CDB4DB] mb-3">
              Friend for Work & Study
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-['Fredoka'] leading-tight mb-5">
              Your Cute ADHD
              <br />
              <span className="text-[#A2D2FF]">Focus Companion</span>
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-md leading-relaxed">
              A cozy productivity app with adorable animals that help you stay focused, build habits, and make getting things done feel rewarding.
            </p>
            <div className="flex gap-3 mb-8">
              <Link
                to="/register"
                className="neo-btn px-8 py-4 bg-[#B9FBC0] text-lg flex items-center gap-2"
                data-testid="hero-cta-btn"
              >
                <span>Start Your Journey</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <DoodleStrip />
          </div>

          {/* Right — Illustrated Character */}
          <div className="relative">
            <div className="neo-card overflow-hidden rounded-[2rem] relative" style={{ aspectRatio: '2/3', maxHeight: '560px' }}>
              {HERO_IMAGES.map((img, i) => (
                <img
                  key={i}
                  src={img.src}
                  alt={img.name}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                    i === currentImage ? 'opacity-100' : 'opacity-0'
                  }`}
                />
              ))}
              {/* Overlay text */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
                <h3 className="text-white text-2xl font-bold font-['Fredoka']">
                  {HERO_IMAGES[currentImage].name}
                </h3>
                <p className="text-white/80 text-sm">
                  {HERO_IMAGES[currentImage].subtitle}
                </p>
              </div>
            </div>
            {/* Image dots */}
            <div className="flex justify-center gap-2 mt-4">
              {HERO_IMAGES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentImage(i)}
                  className={`w-3 h-3 rounded-full border-2 border-black transition-all ${
                    i === currentImage ? 'bg-[#FFC8DD] scale-125' : 'bg-white'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative z-10 bg-white dark:bg-[#1e1e3a] border-t-3 border-b-3 border-black py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold font-['Fredoka'] text-center mb-3">
            Everything You Need to Focus
          </h2>
          <div className="flex justify-center mb-10">
            <DoodleStrip />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div
                  key={i}
                  className="neo-card p-6 hover:scale-[1.02] transition-transform"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div
                    className="w-12 h-12 rounded-2xl border-2 border-black flex items-center justify-center mb-4"
                    style={{ backgroundColor: f.color }}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-bold font-['Fredoka'] mb-2">{f.title}</h3>
                  <p className="text-gray-600 text-sm">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-16">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-['Fredoka'] mb-4">
            Your Pet is Waiting!
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Choose from 4 adorable companions, evolve them through 5 stages, and make productivity feel like a game.
          </p>
          <Link
            to="/register"
            className="neo-btn px-10 py-4 bg-[#FFC8DD] text-xl inline-flex items-center gap-3"
            data-testid="bottom-cta-btn"
          >
            <Heart className="w-6 h-6" />
            <span>Adopt Your Critter</span>
          </Link>
          <div className="flex justify-center mt-8">
            <DoodleStrip />
          </div>
        </div>
      </section>
    </div>
  );
}
